import MuiTypography, { TypographyProps } from "@material-ui/core/Typography"
import { Variant } from "@material-ui/core/styles/createTypography"
import { isArray, isNumber, isString } from "utils/lodash"
import { CSSProperties, PropsWithChildren, useMemo } from "react"

import { AppColor } from "theme/app-color"
import typography from "theme/typography"
import { toCurrency } from "utils/helper"

const CustomVariant = {
  "Body/14": {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "22px",
  },
  "Body/32": {
    fontSize: "32px",
    fontWeight: 700,
    lineHeight: "22px",
  },
  "Body/16": {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "24px",
  },
  "Tag/10": {
    fontSize: "10px",
    fontWeight: 500,
    lineHeight: "16px",
  },
  "Sub Desc": {
    fontSize: "12px",
    fontWeight: 500,
    lineHeight: "16px",
  },
}

const customVariantArr = Object.keys(CustomVariant).join(",")

interface CustomTypographyProps extends Pick<TypographyProps, Exclude<keyof TypographyProps, "color" | "variant">> {
  isCurrency?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
  color?: keyof typeof AppColor | string

  variant: Variant | keyof typeof CustomVariant
}

const Typography = (props: PropsWithChildren<CustomTypographyProps>) => {
  const {
    children,
    color,
    style,
    variant,
    isCurrency = false,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    ...rest
  } = props
  const isHexa = color?.startsWith("#")
  const customColor = isHexa ? color : AppColor[color as keyof typeof AppColor]

  const customVariant = customVariantArr.includes(variant) && CustomVariant[variant as keyof typeof CustomVariant]
  const variantStyle: CSSProperties | undefined =
    customVariantArr.includes(variant) && customVariant
      ? {
          fontSize: customVariant.fontSize,
          fontWeight: customVariant.fontWeight,
          lineHeight: customVariant.lineHeight,
          fontFamily: typography.fontFamily,
        }
      : undefined

  let customRestProps = customColor ? { ...rest, style: { ...style, color: customColor } } : { ...rest, style }

  if (customVariant) customRestProps = { ...customRestProps, style: { ...customRestProps.style, ...variantStyle } }

  const customChildren = useMemo(() => {
    if (isCurrency && children) {
      if (isString(children)) {
        return children.replace(/\d+/g, (match) => toCurrency(match, { minimumFractionDigits, maximumFractionDigits }))
      } else if (isNumber(children)) {
        return isNaN(children) ? 0 : toCurrency(children, { minimumFractionDigits, maximumFractionDigits })
      } else if (isArray(children)) {
        return children.map((item) => {
          if (isNumber(item)) {
            return toCurrency(item, { minimumFractionDigits, maximumFractionDigits })
          }
          return item
        })
      }
    }

    return children
  }, [isCurrency, children, minimumFractionDigits, maximumFractionDigits])

  return (
    <MuiTypography variant={!customVariant ? (variant as Variant) : "inherit"} {...customRestProps}>
      {customChildren}
    </MuiTypography>
  )
}

export default Typography
